import { Pagination } from '@patternfly/react-core'
import {
  Table,
  TableHeader,
  TableBody,
  TableVariant,
} from '@patternfly/react-table'
import * as React from 'react'
import { useCallback, useMemo } from 'react'
import { NavLink } from 'react-router-dom'

import type { IProgram } from '@app/domain/program/program.interface'
import type { IPagination} from '@app/lib/pagination';
import { useHandleCurrentPageChanged } from '@app/lib/pagination'

interface Props {
  programs: IProgram[]
  pagination: IPagination
  getProgramLink: (programId: string) => string
}

const columnNames = [{ title: 'Program Name' }]

export const ProgramListComponent = function ({
  programs,
  pagination,
  getProgramLink,
}: Props) {
  const programToRow = useCallback(
    (program: IProgram) => {
      return {
        cells: [
          <td key={program.programId}>
            <NavLink to={getProgramLink(program.programId)}>
              {program.name}
            </NavLink>
          </td>,
        ],
        props: {
          entryId: program.programId,
        },
      }
    },
    [getProgramLink],
  )

  const programRows = useMemo(() => programs.map(programToRow), [
    programToRow,
    programs,
  ])

  const {
    pageNumber,
    onSetPage,
    onPerPageSelect,
    totalNumberOfRecords,
    recordsPerPage,
  } = pagination

  const handleSetPage = useCallback(
    (_event, updatedPageNumber) => {
      onSetPage(updatedPageNumber)
    },
    [onSetPage],
  )

  const handlePerPageSelect = useCallback(
    (_event, updatedPerPage) => {
      onPerPageSelect(updatedPerPage)
    },
    [onPerPageSelect],
  )

  const { handleCurrentPageChanged } = useHandleCurrentPageChanged(
    onSetPage,
    recordsPerPage,
    totalNumberOfRecords,
  )

  return (
    <React.Fragment>
      <Pagination
        itemCount={totalNumberOfRecords}
        onBlur={handleCurrentPageChanged}
        onChange={handleCurrentPageChanged}
        onPerPageSelect={handlePerPageSelect}
        onSetPage={handleSetPage}
        page={pageNumber}
        perPage={recordsPerPage}
        widgetId="pagination-options-menu-bottom"
      />

      <Table
        aria-label="Sortable Table"
        cells={columnNames}
        rows={programRows}
        variant={TableVariant.compact}
      >
        <TableHeader />
        <TableBody />
      </Table>

      <Pagination
        itemCount={totalNumberOfRecords}
        onBlur={handleCurrentPageChanged}
        onChange={handleCurrentPageChanged}
        onPerPageSelect={handlePerPageSelect}
        onSetPage={handleSetPage}
        page={pageNumber}
        perPage={recordsPerPage}
        widgetId="pagination-options-menu-bottom"
      />
    </React.Fragment>
  )
}
